import { DataBooking, DataEvent, ExtendedDataEvent, ValueType } from '@components/list-card/src/list-card.type';
import { DATE_FORMAT_MONTH_WITH_YEAR, DATE_FORMAT_SHORT_DATE_SHORT_YEAR } from '@constants/datetime';
import { DEFAULT_CURRENCY } from '@constants/global';
import { CompanyStatsResponse } from '@hooks/company/use-get-company-stats';
import { DateRangePickerItemProps } from '@tremor/react';
import {
  addDays,
  getEndOfISOWeek,
  getEndOfMonth,
  getEndOfYear,
  getStartOfISOWeek,
  getStartOfMonth,
  getStartOfYear,
  subtractDays,
  subtractMonths,
  subtractYears,
} from '@utils/date-manipulation';
import { formatCurrency, formatValue } from '@utils/price-formatter/price-formatter';
import { TFunction } from 'i18next';
import moment from 'moment';

export const getChartTabs = (t: TFunction<'translation', undefined>) => [
  {
    id: 'day',
    name: t('eventReport:dayTabLabel'),
  },
  {
    id: 'week',
    name: t('eventReport:weekTabLabel'),
  },
  {
    id: 'month',
    name: t('eventReport:monthTabLabel'),
  },
];

export const currencyValueFormatter = (number: number, locale: string, currency?: string, shortened?: boolean) => {
  return formatCurrency({
    currency: currency ?? DEFAULT_CURRENCY,
    number,
    shortened,
  });
};

export const numberValueFormatter = (number: number, _locale: string) => {
  return formatValue({
    number,
    shortened: true,
  });
};

const getNumber = (val: number | undefined): number => (typeof val === 'number' ? val : 0);
const getTotal = (eventValue: DataEvent, value: ValueType) =>
  getNumber(eventValue?.[`offline_${value}`]?.total) + getNumber(eventValue?.[`online_${value}`]?.total);

const sortedEvents = (eventList: DataEvent[] | undefined, value: ValueType, isOfflineToggleActive: boolean) => {
  if (!eventList) return [];
  const totalEvents = eventList
    .sort((eventA, eventB) => {
      if (isOfflineToggleActive) {
        const totalB = getTotal(eventB, value);
        const totalA = getTotal(eventA, value);
        return totalB - totalA;
      }
      return eventB?.[`online_${value}`]?.total - eventA?.[`online_${value}`]?.total;
    })
    .filter((_, index) => index < 30)
    .sort((eventA, eventB) => eventA?.starting_time - eventB?.starting_time);
  return totalEvents;
};

const formatEventDate = (starting_time: number, dateType?: 'day' | 'week' | 'month'): string => {
  const date = moment(starting_time, 'X');
  if (dateType === 'week') {
    const startWeekDay = date.clone().startOf('week');
    const endWeekDay = date.clone().endOf('week');
    return `${startWeekDay.format(DATE_FORMAT_SHORT_DATE_SHORT_YEAR)} - ${endWeekDay.format(DATE_FORMAT_SHORT_DATE_SHORT_YEAR)}`;
  }
  if (dateType === 'month') return date.format(DATE_FORMAT_MONTH_WITH_YEAR);

  return date.format(DATE_FORMAT_SHORT_DATE_SHORT_YEAR);
};

export const barData = ({
  dateType,
  eventList,
  value,
  showOffline,
  translations,
}: {
  dateType?: 'day' | 'week' | 'month';
  eventList: DataEvent[];
  value: ValueType;
  showOffline: boolean;
  translations: TFunction<'translation', undefined>;
}) =>
  sortedEvents(eventList, value, showOffline).map((event) => {
    const onlineOfferValue = event?.[`online_${value}`].offers || {};
    const offlineOfferValue = event?.[`offline_${value}`].offers || {};
    const onlineAddOnsValue = event?.online_revenue?.add_ons;
    const formattedDate = formatEventDate(event.starting_time, dateType);
    const ticketColumnName = translations('offers:admissionTicket');
    const bottleServiceColumnName = translations('offers:admissionBottleServiceTitle');
    const passColumnName = translations('passes:seasonLabelType');
    const guestListColumnName = translations('offers:admissionGuestListTitle');
    const addonsColumnName = translations('addons:addOnSectionTitle');
    let data;
    const isEventTab = !dateType; // if is booking tab we have date type
    const isRevenueInEventTab = value === 'revenue' && isEventTab;
    if (showOffline) {
      data = {
        starting_time: formattedDate,
        name: `${event.name} (${formattedDate})`,
        [ticketColumnName]:
          getNumber(onlineOfferValue.ticket as number) + getNumber(offlineOfferValue.ticket as number),
        [passColumnName]: getNumber(onlineOfferValue.pass as number) + getNumber(offlineOfferValue.pass as number),
        [bottleServiceColumnName]:
          getNumber(onlineOfferValue.bottle_service as number) + getNumber(offlineOfferValue.bottle_service as number),
        [guestListColumnName]:
          getNumber(onlineOfferValue.guest_list as number) + getNumber(offlineOfferValue.guest_list as number),
      };
      if (isRevenueInEventTab) {
        data = {
          ...data,
          [addonsColumnName]: getNumber((onlineAddOnsValue?.total as number) ?? 0),
        };
      }
      return data;
    }

    data = {
      starting_time: formattedDate,
      name: `${event.name} (${formattedDate})`,
      [ticketColumnName]: getNumber(onlineOfferValue.ticket as number),
      [passColumnName]: getNumber(onlineOfferValue.pass as number),
      [bottleServiceColumnName]: getNumber(onlineOfferValue.bottle_service as number),
      [guestListColumnName]: getNumber(onlineOfferValue.guest_list as number),
    };

    if (isRevenueInEventTab) {
      data = {
        ...data,
        [addonsColumnName]: getNumber((onlineAddOnsValue?.total as number) ?? 0),
      };
    }
    return data;
  });

const formatAnalyiticsDate = (date: number, timezone: string) => {
  return {
    starting_date: moment(date * 1000)
      .tz(timezone)
      .toLocaleString(),
  };
};

export const extendData = (
  analyticsData: DataEvent[] | DataBooking[] | undefined,
  timezone: string,
  _locale: string
): ExtendedDataEvent[] => {
  return (analyticsData ?? []).map((item) => {
    const commonProperties = {
      total_revenue_formatted: formatCurrency({
        currency: item.currency,
        number: item.online_revenue.total + item.offline_revenue.total,
      }),
      total_sold_items_formatted: formatValue({
        number: item.online_sold_items.total + item.offline_sold_items.total,
      }),
      online_revenue: {
        ...item.online_revenue,
        total_formatted: formatCurrency({
          currency: item.currency,
          number: item.online_revenue.total as number,
        }),
      },
      offline_revenue: {
        ...item.offline_revenue,
        total_formatted: formatCurrency({ currency: item.currency, number: item.offline_revenue.total }),
      },
      online_sold_items: {
        ...item.online_sold_items,
        total_formatted: formatValue({ number: item.online_sold_items.total }),
      },
      offline_sold_items: {
        ...item.offline_sold_items,
        total_formatted: formatValue({ number: item.offline_sold_items.total }),
      },
    };
    const date = 'starting_time' in item ? (item as DataEvent).starting_time : (item as DataBooking).date;
    const commonItemProperties = {
      ...item,
      ...commonProperties,
      ...formatAnalyiticsDate(date, timezone),
    };
    return {
      ...commonItemProperties,
      starting_time: date,
    };
  });
};

export const extendStats = (stats: CompanyStatsResponse['stats'] | undefined) => {
  if (!stats) return;
  return {
    ...stats,
    total_revenue: stats.online_revenue.total + stats.offline_revenue.total,
    total_sold_items: stats.online_sold_items.total + stats.offline_sold_items.total,
  };
};

const today = () => new Date();

export interface ExtendedDateRangePickerItemProps extends DateRangePickerItemProps {
  label: string;
}

export const bookingFilterDates = (_locale: string, t: TFunction): ExtendedDateRangePickerItemProps[] => [
  //Last Week: last week 1st to last day
  {
    from: getStartOfISOWeek(subtractDays(today(), 7)),
    to: getEndOfISOWeek(subtractDays(today(), 7)),
    value: 'LW',
    label: t('analytics:datePickerLabelLastWeek'),
  },
  //Last 7 days: today - 7 days
  {
    from: subtractDays(today(), 7),
    to: today(),
    value: 'L7D',
    label: t('analytics:datePickerLabelLast7Days'),
  },
  //Last Month: last month 1st to last day
  {
    from: getStartOfMonth(subtractMonths(today(), 1)),
    to: getEndOfMonth(subtractMonths(today(), 1)),
    value: 'LM',
    label: t('analytics:datePickerLabelLastMonth'),
  },
  //Last 30 days --> default: today - 30 days
  {
    from: subtractDays(today(), 30),
    to: today(),
    value: 'L30D',
    label: t('analytics:datePickerLabelLast30Days'),
  },
  //Month to date: last month 1st to today
  {
    from: getStartOfMonth(subtractMonths(today(), 1)),
    to: today(),
    value: 'MTD',
    label: t('analytics:datePickerLabelMonthToDate'),
  },
  //Last Year: today - 365 days
  {
    from: subtractDays(today(), 365),
    to: today(),
    value: 'LY',
    label: t('analytics:datePickerLabelLastYear'),
  },
  //Year to date: 1 Jan current year to today.
  {
    from: getStartOfYear(today()),
    to: today(),
    value: 'CY',
    label: t('analytics:datePickerLabelCurrentYear'),
  },
];

export const eventFilterDates = (_locale: string, t: TFunction): ExtendedDateRangePickerItemProps[] => [
  // Upcoming (= today + 365 days) --> default
  {
    from: today(),
    to: addDays(today(), 365),
    value: 'UY',
    label: t('analytics:datePickerLabelUpcomingYear'),
  },
  // This week (from this week monday to today)
  {
    from: getStartOfISOWeek(today()),
    to: getEndOfISOWeek(today()),
    value: 'CW',
    label: t('analytics:datePickerLabelCurrentWeek'),
  },
  // This month (from day 1 to today)
  {
    from: getStartOfMonth(today()),
    to: getEndOfMonth(today()),
    value: 'CM',
    label: t('analytics:datePickerLabelCurrentMonth'),
  },
  // Last month (from day 1 to last day of last month)
  {
    from: getStartOfMonth(subtractMonths(today(), 1)),
    to: getEndOfMonth(subtractMonths(today(), 1)),
    value: 'LM',
    label: t('analytics:datePickerLabelLastMonth'),
  },
  // This Year (from day Jan 1st current year to today)
  {
    from: getStartOfYear(today()),
    to: getEndOfYear(today()),
    value: 'CY',
    label: t('analytics:datePickerLabelCurrentYear'),
  },
  // Last Year (from day Jan 1st current year to end of last year)
  {
    from: getStartOfYear(subtractYears(today(), 1)),
    to: getEndOfYear(subtractYears(today(), 1)),
    value: 'LY',
    label: t('analytics:datePickerLabelPastYear'),
  },
];
